import { Typography, useEventCallback } from '@mui/material';
import { Fragment, ReactElement, useEffect, useState } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { PracticeResearchCrumbs } from 'practice/constants/practice-crubs';
import TablePractice from 'practice/components/research/table-practice';
import {
  FilterPersonVM,
  FilterPracticeVM,
  PracticeDataVM,
  PracticeVM,
} from 'practice/view-models/practice-research';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import {
  CustumPracticeHttpResponseList,
  transformerPraticeList,
  transformPracticeSearchFiltersData,
} from 'practice/utils/practice-research-transform';
import { PracticeData } from 'shell/models/practice';
import Filter from 'practice/components/shared/filter';
import { Operation } from 'shell/view-models/operation';
import { paginationListToQueryString } from 'system/utils/list-object-to-query-string';
import { OperationObject } from 'system/utils/event-batch/filters-object-to-query-string-batch-event';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { PracticeSearchType, PracticeFilterSearchType } from '../storage-practice';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useUserProfile } from 'shell/hooks/use-user-profile';

const PracticeResearchPage = (): ReactElement => {
  const [filterSelected, setFilterSelected] = useState<OperationObject>({});

  const [withResponse, setWithResponse] = useState<boolean>(false);

  const { userProfile } = useUserProfile();
  const authPerson = userProfile.datiLogin?.profile?.feature?.filtroPraticaServizioOperatore || '';
  const userOperator = userProfile.datiLogin?.key?.userName;

  const filterTable = useFilterTable<PracticeSearchType>();
  const [isForPersonSearch, setIsPersonSearch] = useState<boolean>(false);
  const [praticeList, getPraticeList] = useHttpFetch<
    CustumPracticeHttpResponseList<PracticeData>,
    PracticeDataVM
  >(`${__PRACTICE_API_URL__}/pratiche`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformerPraticeList,
  });

  useEffect(() => {
    if (
      filterTable?.values?.searchType === PracticeFilterSearchType.SEARCH_BY_PRATICE &&
      filterTable?.values?.isChildComponentVisited
    ) {
      const data: FilterPracticeVM = {
        typeRegister: filterTable?.values?.typeRegister,
        years: filterTable?.values?.years,
        fromNumber: filterTable?.values?.fromNumber,
        atNumber: filterTable?.values?.atNumber,
        dateFrom: filterTable?.values?.dateFrom,
        dateTo: filterTable?.values?.dateTo,
        dateClosure: filterTable?.values?.dateClosure,
        dateExpiration: filterTable?.values?.dateExpiration,
        prioritaSenzaAssensoOspitante: filterTable?.values?.prioritaSenzaAssensoOspitante,
        // service: filterTable?.values?.service,
        operator: filterTable?.values?.operator,
        state: filterTable?.values?.state || '',
        practicePlacementService: filterTable?.values?.practicePlacementService,
        acquisitionSystem: filterTable?.values?.acquisitionSystem,
        lastTypeOperation: filterTable?.values?.lastTypeOperation,
        numberAnpr: filterTable?.values?.numberAnpr,
        progressive: filterTable?.values?.progressive,
        origin: filterTable?.values.origin,
        onlyPracticeAssigned: filterTable?.values.onlyPracticeAssigned,
        italianCitizenship: filterTable?.values?.italianCitizenship,
        foreignCitizenship: filterTable?.values?.foreignCitizenship,
        irreperibileMilano: filterTable?.values?.irreperibileMilano,
        irreperibileAltroComune: filterTable?.values?.irreperibileAltroComune,
        accertamentoPositivo: filterTable?.values?.accertamentoPositivo,
        accertamentoNegativo: filterTable?.values?.accertamentoNegativo,
        accertamentoDubbio: filterTable?.values?.accertamentoDubbio,
        dataDecorrenzaDa: filterTable?.values?.dataDecorrenzaDa,
        dataDecorrenzaA: filterTable?.values?.dataDecorrenzaA,
      };
      const transformedData = transformPracticeSearchFiltersData(data);
      getPraticeListData(transformedData);
    }
  }, []);

  const getPraticeListData = useEventCallback(
    (dataFilter: OperationObject, page?: number, rowsPerPage?: number) => {
      const queryParams = `${paginationListToQueryString({
        filters: dataFilter,
        orders: {},
        page: page || 0,
        rowsPerPage: rowsPerPage || 10,
      })}`;

      return (
        getPraticeList({
          url: `${
            isForPersonSearch ||
            (filterTable?.values?.searchType === PracticeFilterSearchType.SEARCH_BY_PERSON &&
              filterTable?.values?.isChildComponentVisited)
              ? '/persona'
              : ''
          }`,
          params: {},
          paramsSerializer: () => queryParams,
        }) as ResponsePromise<CustumPracticeHttpResponseList<PracticeData>, PracticeDataVM>
      ).then((response) => {
        const castedResponse = response as PracticeDataVM;

        if ((castedResponse?.list || [])?.length > 0) {
          setWithResponse(true);
        }

        setIsPersonSearch(false);
        setFilterSelected(dataFilter);
      });
    }
  );

  const onSubmitPerson = useEventCallback((person: FilterPersonVM) => {
    setWithResponse(false);

    // effettuare ricerca pratiche per codice o nome e cognome
    filterTable.setValues({
      ...filterTable?.values,
      ...person,
      searchType: PracticeFilterSearchType.SEARCH_BY_PERSON,
    });
    const data = {
      codiceFiscale: { value: person?.fiscalCode || '', operation: Operation.EQUALS },
      nome: { value: person?.name || '', operation: Operation.EQUALS },
      cognome: { value: person?.surname || '', operation: Operation.EQUALS },
      dataNascita: {
        value: person?.birthDate?.toFormat('yyyy-MM-dd') || '',
        operation: Operation.EQUALS,
      },
    };
    setIsPersonSearch(true);
    getPraticeListData(data);
  });

  const onSubmitFilterPractice = useEventCallback((values: FilterPracticeVM) => {
    setWithResponse(false);

    filterTable.setValues({
      ...filterTable?.values,
      ...values,
      atNumber: values?.atNumber || null,
      fromNumber: values?.fromNumber || null,
      numberAnpr: values?.numberAnpr || null,
      searchType: PracticeFilterSearchType.SEARCH_BY_PRATICE,
    });
    const transformedData = transformPracticeSearchFiltersData(values);
    getPraticeListData(transformedData);
  });

  return (
    <Fragment>
      <Breadcrumbs items={PracticeResearchCrumbs} />
      <div className="pmi-pt-4 pmi-pb-3 pmi-d-flex pmi-align-items-center pmi-justify-content-between">
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          Ricerca pratiche
        </Typography>
      </div>
      <div className="circle" />
      <Filter
        onSubmitFilterPractice={onSubmitFilterPractice}
        onSubmitPerson={onSubmitPerson}
        authPerson={authPerson}
        userOperator={userOperator}
        withResponse={withResponse}
        isResearch
        practiceLength={
          (((praticeList.data as PracticeDataVM)?.list as Array<PracticeVM>) || [])?.length || 0
        }
      />
      {praticeList.data ? (
        (((praticeList.data as PracticeDataVM)?.list as Array<PracticeVM>) || [])?.length > 0 ? (
          <>
            <TablePractice
              data={praticeList.data as PracticeDataVM}
              filterSelected={filterSelected}
              getPraticeListData={getPraticeListData}
            />
          </>
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

PracticeResearchPage.displayName = 'Practice Research Page component';

export default PracticeResearchPage;
